/**
 * components/ExportButton.js — Phase 3
 *
 * Reusable export toolbar that appears on every dashboard tab.
 * Supports PDF (html2canvas + jsPDF) and Excel (SheetJS).
 *
 * Install:
 *   npm install jspdf html2canvas xlsx
 *
 * Usage:
 *   <ExportButton
 *     targetId="finance-tab-content"
 *     filename="finance_budget"
 *     title="Finance & Budget Report"
 *     excelSheets={[{ name: 'Payroll', data: rows, columns: [...] }]}
 *   />
 */

import React, { useState } from 'react';
import { Box, Button, Menu, MenuItem, ListItemIcon, ListItemText, CircularProgress, Tooltip } from '@mui/material';
import { PictureAsPdf, TableChart, FileDownload } from '@mui/icons-material';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDateTag() {
  const d  = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}${mm}${dd}`;
}

// ─── PDF export ───────────────────────────────────────────────────────────────

async function exportToPdf(elementId, filename, title, subtitle) {
  const el = document.getElementById(elementId);
  if (!el) throw new Error(`#${elementId} not found`);

  // Force white background for PDF rendering
  const origBg = el.style.background;
  el.style.background = '#ffffff';

  try {
    const canvas = await html2canvas(el, {
      scale:           2,
      useCORS:         true,
      backgroundColor: '#ffffff',
      logging:         false,
      // Scroll to top of element before capturing
      windowWidth:     el.scrollWidth,
      windowHeight:    el.scrollHeight,
    });

    const imgData  = canvas.toDataURL('image/png');
    const isLandscape = canvas.width > canvas.height;
    const pdf      = new jsPDF({ orientation: isLandscape ? 'landscape' : 'portrait', unit: 'mm', format: 'a4' });
    const pageW    = pdf.internal.pageSize.getWidth();
    const pageH    = pdf.internal.pageSize.getHeight();
    const margin   = 12;

    // Header
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(15);
    pdf.setTextColor(21, 101, 192);       // MUI primary blue
    pdf.text(title, margin, margin + 5);

    if (subtitle) {
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(9);
      pdf.setTextColor(100, 100, 100);
      pdf.text(subtitle, margin, margin + 11);
    }

    pdf.setFontSize(8);
    pdf.setTextColor(160, 160, 160);
    pdf.text(`Generated: ${new Date().toLocaleString('en-MY')}`, margin, margin + 17);

    // Rule line
    pdf.setDrawColor(200, 200, 200);
    pdf.line(margin, margin + 20, pageW - margin, margin + 20);

    // Content image — scale to fit page width
    const headerH   = margin + 24;
    const availW    = pageW - margin * 2;
    const availH    = pageH - headerH - margin;
    const imgAspect = canvas.width / canvas.height;
    const imgH      = Math.min(availH, availW / imgAspect);
    const imgW      = imgH * imgAspect;

    pdf.addImage(imgData, 'PNG', margin, headerH, imgW, imgH);

    // Footer
    pdf.setFontSize(7);
    pdf.setTextColor(180, 180, 180);
    pdf.text('HRMS Dashboard — Confidential', margin, pageH - 5);
    pdf.text('Page 1', pageW - margin - 10, pageH - 5);

    pdf.save(`${filename}_${formatDateTag()}.pdf`);
  } finally {
    el.style.background = origBg;
  }
}

// ─── Excel export ─────────────────────────────────────────────────────────────

function exportToExcel(sheets, filename, title) {
  const workbook = XLSX.utils.book_new();

  // Summary sheet
  const summaryWs = XLSX.utils.aoa_to_sheet([
    ['Report',       title],
    ['Generated at', new Date().toLocaleString('en-MY')],
    ['System',       'HRMS Dashboard'],
  ]);
  summaryWs['!cols'] = [{ wch: 14 }, { wch: 40 }];
  XLSX.utils.book_append_sheet(workbook, summaryWs, 'Summary');

  // Data sheets
  sheets.forEach(({ name, data, columns }) => {
    if (!data?.length) return;

    const headers = columns ? columns.map((c) => c.label) : Object.keys(data[0]);
    const keys    = columns ? columns.map((c) => c.key)   : Object.keys(data[0]);

    const rows = data.map((row) =>
      keys.map((k) => {
        const col = columns?.find((c) => c.key === k);
        const val = row[k];
        if (col?.format === 'date' && val) return new Date(val);
        return val;
      }),
    );

    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);

    // Column widths
    if (columns) {
      ws['!cols'] = columns.map((c) => ({ wch: c.width ?? 18 }));
    }

    // Number formats
    if (columns) {
      data.forEach((_, ri) => {
        columns.forEach((col, ci) => {
          const ref  = XLSX.utils.encode_cell({ r: ri + 1, c: ci });
          const cell = ws[ref];
          if (!cell) return;
          if (col.format === 'currency') cell.z = '#,##0.00';
          if (col.format === 'percent')  cell.z = '0.0%';
          if (col.format === 'integer')  cell.z = '#,##0';
          if (col.format === 'date')     cell.z = 'yyyy-mm-dd';
        });
      });
    }

    // Freeze header row
    ws['!freeze'] = { xSplit: 0, ySplit: 1 };

    XLSX.utils.book_append_sheet(workbook, ws, name.slice(0, 31));
  });

  XLSX.writeFile(workbook, `${filename}_${formatDateTag()}.xlsx`);
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function ExportButton({
  targetId,       // id of the DOM element to screenshot for PDF
  filename,       // base filename without extension
  title,          // human-readable report title
  subtitle = '',  // optional subtitle line in PDF header
  excelSheets = [], // [{ name, data, columns }]
  size = 'small',
}) {
  const [anchor,  setAnchor]  = useState(null);
  const [loading, setLoading] = useState(null); // 'pdf' | 'excel' | null

  const handlePdf = async () => {
    setAnchor(null);
    setLoading('pdf');
    try {
      await exportToPdf(targetId, filename, title, subtitle);
    } catch (e) {
      console.error('PDF export failed', e);
    } finally {
      setLoading(null);
    }
  };

  const handleExcel = () => {
    setAnchor(null);
    setLoading('excel');
    try {
      exportToExcel(excelSheets, filename, title);
    } catch (e) {
      console.error('Excel export failed', e);
    } finally {
      setLoading(null);
    }
  };

  return (
    <>
      <Tooltip title="Export report">
        <span>
          <Button
            size={size}
            variant="outlined"
            startIcon={loading ? <CircularProgress size={14} /> : <FileDownload />}
            onClick={(e) => setAnchor(e.currentTarget)}
            disabled={!!loading}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {loading === 'pdf' ? 'Generating PDF…' : loading === 'excel' ? 'Generating Excel…' : 'Export'}
          </Button>
        </span>
      </Tooltip>

      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}
        PaperProps={{ sx: { minWidth: 180, borderRadius: 2, mt: 0.5 } }}>
        <MenuItem onClick={handlePdf} disabled={!targetId}>
          <ListItemIcon><PictureAsPdf fontSize="small" sx={{ color: '#C62828' }} /></ListItemIcon>
          <ListItemText>Export as PDF</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleExcel} disabled={!excelSheets.length}>
          <ListItemIcon><TableChart fontSize="small" sx={{ color: '#1B5E20' }} /></ListItemIcon>
          <ListItemText>Export as Excel</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}
